import request from "@/plugin/axios";
import { commonStoreShell } from '@/common/StoreShell';
import { MenuDescriptor } from './declare';
import { getLocaleAlias } from "@/common/18nhelper";
import router from "@/router";
import { Route } from "vue-router";
import { broadcast, BroadcastType } from "./BroadcastStore";

export function getAuthData(): Promise<MenuDescriptor[]> {
    // 获取当前用户的权限数据，用于判断按钮显示与否
    return request({
        url: '/union/Admin/getMyAuth',
        method: 'POST',
    }).then(res => res.data);
}

interface AccessControlState {
    /**menuId(菜单标识符) => perms(菜单权限)映射 */
    permObject: Record<string, Array<string>>
    /**url(菜单路由) => {parent, name}(父级路由,路由名字)映射 */
    urlChain: Record<string, {
        parent: string | ((route: Route) => string)
        name: string
    }>
    currentMenu: string
}

interface AccessControlGetters {
    auth: () => Record<string, boolean>
    /** 测试用,授予所有权限 */
    grantAll: () => Record<string, boolean>
}

class AccessStore extends commonStoreShell<AccessControlState, AccessControlGetters> {
    public getters = {
        auth: () => {
            if (this.state.currentMenu in this.state.permObject) {
                return this.state.permObject[this.state.currentMenu].reduce((prev, current) => {
                    prev[current] = true;
                    return prev;
                }, {})
            }
            else {
                return {};
            }
        },
        grantAll: () => {
            return {
                query: true,
                edit: true,
                add: true,
                audit: true,
                recall: true,
                detail: true,
                remove: true,
            }
        }
    }

    protected getStateInit(): AccessControlState {
        this.reload();

        return {
            permObject: {},
            currentMenu: '',
            urlChain: {},
        }
    }

    public reload() {
        return getAuthData().then((res) => {
            for (let item of res) {
                // 构建权限信息
                if (item.parentId! in this.state.permObject) {
                    this.state.permObject[item.parentId!].push(item.perms);
                }
                else {
                    this.state.permObject[item.parentId!] = [item.perms];
                }

                // 构建路由url信息
            }
        })
    }

    /**
     * 构建url信息以为autoNav组件提供url导航支持
     * @param src 接口菜单数据
     */
    public loadUrlChain(src: MenuDescriptor[]) {
        this.state.urlChain = {};
        const id = (src: MenuDescriptor) => {
            if (src.url) {
                return '/' + src.url.slice(1).replace(/\//g, '-');
            }
            return src.menuId;
        }

        const recursion = (src: MenuDescriptor[], parent?: MenuDescriptor) => {
            for (let item of src) {
                // 由于CATALOG顶级路由没有url,此时采用id替代,也就是说parent会出现混用id与url的情况
                this.state.urlChain[id(item)] = {
                    parent: parent ? id(parent) : '',
                    name: item.nameText == null? '':item.nameText[getLocaleAlias()]!,
                }

                if (item.menuList) {
                    recursion(item.menuList, item);
                }
            }
        }

        recursion(src);
        this.hardCodeUrlChain();

        broadcast.sendByName(BroadcastType.APP_LocaleChange);
    }

    /**由于后端所有MENU级菜单都是平级的,不支持树状结构,构建url完成之后,在此用硬编码方式补充MENU之间的父子关系 */
    private hardCodeUrlChain() {
        // 左边为子级,右边为父级
        const hardCoding = {
            '/Central-addStock': '/Central-stockList',
            '/Central-editStock': '/Central-stockList',
            '/Central-addressList': '/Central-partnerList',
            '/Central-partnerForm': '/Central-partnerList',
            '/Central-addressForm': '/Central-addressList',
            '/Central-sellerForm': '/Central-seller',

            '/Sap-SapCommonDetail': (route: Route) => {
                switch (route.query.type) {
                    case 'sell':
                        return '/Sap-SapSellList';
                    case 'ship':
                        return '/Sap-SapShipList';
                    case 'inv':
                        return '/Sap-SapInvoiceList';
                    case 'ret':
                        return '/Sap-SapReturnList';
                }
            },
            
            '/Warehouse-afterIntoDetail': '/Warehouse-afterInto',
            '/Warehouse-afterIntoForm': '/Warehouse-afterInto',
            '/Warehouse-returnIntoDetail': '/Warehouse-returnInto',
            '/Warehouse-returnIntoForm': '/Warehouse-returnInto',
            '/Warehouse-importDetail': '/Warehouse-intoWarehouse',
            '/Warehouse-intoForm': '/Warehouse-intoWarehouse',
            '/Warehouse-exportDetail': '/Warehouse-outWarehouse',
            '/Warehouse-outForm': '/Warehouse-outWarehouse',
            '/Warehouse-otherOutDetail': '/Warehouse-otherOutWarehouse',
            '/Warehouse-otherOutForm': '/Warehouse-otherOutWarehouse',
            '/Warehouse-allocDetail': '/Warehouse-allocList',
            '/Warehouse-allocForm': '/Warehouse-allocList',
            '/Warehouse-crossDetail': '/Warehouse-crossWarehouse',
            '/Warehouse-crossForm': '/Warehouse-crossWarehouse',
            '/Warehouse-log': '/Warehouse-virtualSaleList',
            '/Warehouse-kerryPassDetail': '/Warehouse-kerryPassList',
            '/Warehouse-kerryPassForm': '/Warehouse-kerryPassList',

            '/Sale-hqQuotationForm': '/Sale-hqQuotation',
            '/Sale-hqQuotationDetail': '/Sale-hqQuotation',
            '/Sale-quotationForm': '/Sale-quotationList',
            '/Sale-quotationDetail': '/Sale-quotationList',
            '/Sale-hqOrderForm': '/Sale-hqOrderList',
            '/Sale-hqOrderDetail': '/Sale-hqOrderList',
            '/Sale-orderDetail': (route: Route) => {
                if (route.query.audit && route.query.audit === 'Y') {
                    return '/Sale-orderListAudit'
                }
                else {
                    return '/Sale-orderList'
                }
            },
            '/Sale-hqDeliveryDetail': '/Sale-hqDelivery',
            '/Sale-deliveryDetail': '/Sale-delivery',

            '/AfterSale-rejReturnDetail': '/AfterSale-rejReturn',
            '/AfterSale-asReturnDetail': '/AfterSale-asReturn',
            '/AfterSale-orderReturnDetail': (route: Route) => {
                if (route.query.from && route.query.from === 'as') {
                    return '/AfterSale-asOrderList'
                }
                else {
                    return '/AfterSale-rejOrderList'
                }
            },
            '/AfterSale-orderReturnForm': (route: Route) => {
                if (route.query.from && route.query.from === 'as') {
                    return '/AfterSale-asOrderList'
                }
                else {
                    return '/AfterSale-rejOrderList'
                }
            },
        }

        for (let url in hardCoding) {
            // 防止hardcode出错炸掉整个路由,使用tryCatch进行提示
            try {
                // this.state.urlChain[url].parent = hardCoding[url];
            }
            catch (e) {
                console.error(e);
            }
        }
    }

    /**解析路由链,用作导航信息 */
    public resolveUrlChain() {
        const ret = [] as any[];

        let url = router.currentRoute.path
        let entry = this.state.urlChain[url];

        while (entry !== undefined) {
            ret.unshift({
                to: url,
                label: entry.name,
            })

            url = typeof entry.parent === 'string' ? entry.parent : entry.parent(router.currentRoute);
            entry = this.state.urlChain[url];
        }
        // 防止重复导航和404,重新设置首尾的to参数为空
        ret[0].to = '';
        ret[ret.length - 1].to = '';

        return ret;
    }
}

const access = new AccessStore();

export { access };